import React, { useState, useEffect } from "react";
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import ExpenseModal from "./components/ExpenseModal";
import './App.css';
import AppointmentNotification from "./components/AppointmentNotification";
import { Bell, Menu } from "lucide-react";
import { Dog, Cat, Trash2 } from "lucide-react";
import Login from "./components/Login.js";
import RegisterUser from "./components/RegisterUser.js";
import SessionTimeoutModal from "./components/SessionTimeoutModal";
import UsersModal from "./components/UsersModal";
import StockModal from "./components/StockModal";
import BillingModal from "./components/BillingModal";
import AppointmentModal from "./components/AppointmentModal";
import DoctorAppointments from "./components/DoctorAppointments";
import LowStockAlert from "./components/LowStockAlert";
import ExpiryAlert from "./components/ExpiryAlert";
import RecentBillsModal from "./components/RecentBillsModal";
import SalesReportModal from "./components/SalesReportModal";
import GstItemsModal from "./components/GstItemsModal";
import BloodTestModal from "./components/BloodTestModal";
import BloodTestAdminModal from "./components/BloodTestAdminModal";
import FollowUpConsultation from "./components/FollowUpConsultation";
import UserProfileModal from "./components/UserProfileModal";
import MiscellaneousBillModal from "./components/MiscellaneousBillModal";
import HomeAppointmentsPanel from "./components/HomeAppointmentsPanel";
import OnlineConsultationModal from "./components/OnlineConsultationModal";
import GooglePosts from "./components/GoogleBusinessPost.js";
import GBPMatrixModal from "./components/GBPMatrixModal";
import ChatWindow from "./components/ChatWindow";
import LeaveTrackerModal from "./components/StaffLeaveTracker";
import { formatISTDateTime, getISTDateInputValue, getISTDateParts, parseISTTimestamp } from "./utils/dateTime";

const BASE_URL = "/v1";

const speciesList = ["Dog", "Cat", "Rabbit", "Bird", "Exotic"];
const googlePostCategories = ["dog", "cat", "birds", "sugar glider", "rabbit", "hamster", "turtle", "guinea pig"];
const ageUnitOptions = ["Days", "Weeks", "Months", "Years"];
const petOwnerBehaviorOptions = [
  "Pays fees on time",
  "Payment Issues",
  "Punctual and cooperative",
  "Follows treatment instructions well",
  "Needs reminders for payment",
  "Frequently delays payment",
  "Shows good pet care and hygiene",
  "Needs counseling on pet care",
  "Respectful and communicative",
  "Concerned but needs guidance",
  "Inconsistent with follow-up visits",
  "Regular with vaccinations",
  "Misses scheduled appointments",
  "Very responsive during emergencies",
  "Good medication compliance",
  "Difficulty administering medicines",
  "Overprotective pet owner",
  "Financially cooperative",
  "Frequently changes treatment decisions",
  "Trusts veterinary guidance",
  "Needs repeated explanations",
  "Maintains proper grooming",
  "Poor hygiene maintenance",
  "Responsible first-time pet owner",
  "Experienced pet handler",
  "Aggressive behavior towards staff",
  "Friendly and understanding",
  "Frequently requests discounts",
  "Good follow-up compliance",
  "Delayed response during emergencies",
  "Maintains pet diet properly",
  "Needs education on nutrition",
  "Cooperative during hospitalization",
  "Irregular medication compliance",
  "Frequently misses medication doses",
  "Brings pet for regular checkups",
  "Non-compliant with treatment plan",
  "Very attentive to pet health",
  "Anxious about pet condition",
  "Difficult to contact",
  "Quick decision maker",
  "Seeks multiple opinions frequently",
  "Highly informed pet owner",
  "Needs basic pet care awareness"
];

const buildAgeString = (age, unit) => {
  const value = String(age || "").trim();
  if (!value) return "";
  return `${value} ${unit || "Years"}`;
};

const parseAgeString = (age) => {
  const raw = String(age || "").trim();
  if (!raw) {
    return { ageValue: "", ageUnit: "Years" };
  }

  const match = raw.match(/^(\d+(?:\.\d+)?)\s*(days?|weeks?|months?|years?)?$/i);

  if (!match) {
    return { ageValue: raw, ageUnit: "Years" };
  }

  const value = match[1] || "";
  const unitRaw = (match[2] || "Years").toLowerCase();
  const unit =
    unitRaw.startsWith("day") ? "Days" :
      unitRaw.startsWith("week") ? "Weeks" :
        unitRaw.startsWith("month") ? "Months" :
          "Years";

  return { ageValue: value, ageUnit: unit };
};

const formatPetAge = (age) => {
  const value = String(age || "").trim();
  if (!value) return "-";
  return /[a-z]/i.test(value) ? value : `${value} Years`;
};

const formatVisitAge = (visit) => {
  const direct = String(visit?.age ?? "").trim();
  if (direct) return direct;

  const fromParts = buildAgeString(visit?.ageValue, visit?.ageUnit);
  return fromParts || "-";
};

const splitPetOwnerBehavior = (value) =>
  String(value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const isVisitAssignedToDoctor = (visit) =>
  Boolean(visit?.doctorId || String(visit?.docName || "").trim());

const canEditPrescriptionForAppointment = (appointment, user) =>
  Number(user?.role) === 2 &&
  Number(appointment?.doctorId) === Number(user?.doc?.id) &&
  Boolean(appointment?.doctorId);

const togglePetOwnerBehavior = (currentValue, option) => {
  const current = splitPetOwnerBehavior(currentValue);
  const next = current.includes(option)
    ? current.filter((item) => item !== option)
    : [...current, option];

  return next.join(", ");
};

const isValidMobile = (mobile) => {
  return /^[6-9]\d{9}$/.test(mobile); // Indian mobile numbers
};


const catBreeds = ["Persian", "Persian Mix", "Siamese", "Maine Coon", "Ragdoll", "British Shorthair", "British Longhair",
  "Bengal", "Sphynx", "Abyssinian", "Himalayan", "Scottish Fold", "Scottish Straight", "Birman", "Oriental Shorthair",
  "Oriental Longhair", "Russian Blue", "Norwegian Forest Cat", "Siberian", "Turkish Angora", "Turkish Van", "American Shorthair",
  "American Curl", "American Bobtail", "Japanese Bobtail", "Manx", "Bombay", "Burmese", "Tonkinese", "Balinese", "Devon Rex",
  "Cornish Rex", "Selkirk Rex", "LaPerm", "Ocicat", "Savannah", "Egyptian Mau", "Chartreux", "Singapura", "Snowshoe",
  "Exotic Shorthair", "Munchkin", "Pixie-Bob", "Toyger", "Somali", "Ragamuffin", "Nebelung", "Korat", "Lykoi", "Peterbald",
  "Chausie", "Burmilla", "Cymric", "Domestic Shorthair", "Domestic Longhair", "Indie (Domestic Cat)", "Mixed Breed", "Other"];

const rabbitBreeds = ["Netherland Dwarf", "Holland Lop", "Mini Lop", "Mini Rex", "Rex", "Lionhead", "Flemish Giant",
  "English Angora", "French Angora", "Satin Angora", "English Lop", "French Lop", "American Fuzzy Lop", "Mini Satin",
  "Satin", "Dutch", "Harlequin", "Californian", "New Zealand White", "New Zealand Red", "Polish", "Himalayan",
  "English Spot", "Checkered Giant", "Silver Fox", "Champagne d'Argent", "Beveren", "Belgian Hare", "Jersey Wooly",
  "Florida White", "Mini Plush Lop", "Dwarf Hotot", "American Sable", "Tan", "Thrianta", "Cinnamon", "Velveteen Lop",
  "Continental Giant", "Netherland Dwarf Mix", "Mixed Breed", "Indian Local", "Other"];

const birdBreeds = ["Parrot", "African Grey", "Cockatiel", "Budgerigar (Budgie)", "Lovebird", "Canary",
  "Finch", "Macaw", "Conure", "Cockatoo", "Pigeon", "Myna", "Indian Ringneck", "Amazon Parrot", "Eclectus Parrot",
  "Quaker Parrot (Monk Parakeet)", "Senegal Parrot", "Caique", "Lorikeet", "Lory", "Rosella", "Bourke's Parakeet",
  "Alexandrine Parakeet", "Plum-headed Parakeet", "Derbyan Parakeet", "Sun Conure", "Green-cheeked Conure",
  "Jenday Conure", "Scarlet Macaw", "Blue-and-Gold Macaw", "Hyacinth Macaw", "Umbrella Cockatoo",
  "Sulphur-crested Cockatoo", "Goffin's Cockatoo", "Galah Cockatoo", "Zebra Finch", "Society Finch",
  "Gouldian Finch", "Java Sparrow", "Peacock", "Chicken", "Duck", "Goose", "Turkey", "Quail", "Guinea Fowl",
  "Pheasant", "Ostrich", "Emu", "Owl", "Eagle", "Falcon", "Kingfisher", "Woodpecker", "Hornbill", "Toucan",
  "Flamingo", "Pelican", "Penguin", "Crow", "Raven", "Bulbul", "Koel", "Cuckoo", "Sunbird", "Hummingbird", "Other"];

const exoticBreeds = ["Ferret", "Guinea Pig", "Hamster", "Chinchilla", "Hedgehog", "Sugar Glider", "Rabbit", "Mouse",
  "Rat", "Gerbil", "Degu", "Prairie Dog", "Skunk", "Raccoon", "Axolotl", "Turtle", "Tortoise", "Terrapin", "Bearded Dragon",
  "Leopard Gecko", "Crested Gecko", "Tokay Gecko", "African Fat-tailed Gecko", "Iguana", "Green Iguana", "Blue-Tongued Skink",
  "Uromastyx", "Monitor Lizard", "Chameleon", "Veiled Chameleon", "Panther Chameleon", "Anole", "Basilisk", "Tegu", "Snake",
  "Ball Python", "Corn Snake", "King Snake", "Milk Snake", "Boa Constrictor", "Rosy Boa", "Sand Boa", "Garter Snake",
  "Hognose Snake", "Kingsnake", "California Kingsnake", "Boa", "Python", "Frog", "Tree Frog", "Pacman Frog", "Toad",
  "Salamander", "Newt", "Hermit Crab", "Tarantula", "Scorpion", "Millipede", "Stick Insect", "Praying Mantis", "Other"];


const dogBreeds = ["Labrador Retriever", "Golden Retriever", "German Shepherd", "Poodle", "Rottweiler", "Doberman Pinscher",
  "Pug", "Beagle", "Dachshund", "Shih Tzu", "Chihuahua", "Siberian Husky", "Border Collie", "Pomeranian", "Great Dane", "Boxer",
  "Cocker Spaniel", "English Cocker Spaniel", "American Cocker Spaniel", "Dalmatian", "Saint Bernard", "Bulldog", "French Bulldog",
  "Bull Terrier", "Boston Terrier", "American Bully", "Maltese", "Yorkshire Terrier", "Bichon Frise", "Havanese", "Lhasa Apso",
  "Tibetan Terrier", "Tibetan Mastiff", "Akita", "Shiba Inu", "Alaskan Malamute", "Samoyed", "Chow Chow", "Shar Pei", "Basenji",
  "Afghan Hound", "Greyhound", "Whippet", "Irish Wolfhound", "Newfoundland", "Bernese Mountain Dog", "Australian Shepherd",
  "Australian Cattle Dog", "Shetland Sheepdog", "Collie", "Belgian Malinois", "Dutch Shepherd", "Cane Corso", "Mastiff",
  "Bullmastiff", "Neapolitan Mastiff", "Kangal", "Basset Hound", "Bloodhound", "Fox Terrier", "Jack Russell Terrier",
  "Parson Russell Terrier", "Miniature Schnauzer", "Giant Schnauzer", "Standard Schnauzer", "Vizsla", "Weimaraner", "Pointer",
  "Setter", "Brittany Spaniel", "Papillon", "Pekingese", "Japanese Spitz", "Keeshond", "Pomeranian Mix", "Rajapalayam",
  "Mudhol Hound", "Chippiparai", "Kombai", "Rampur Hound", "Bakharwal Dog", "Gaddi Dog", "Indian Pariah Dog (Indie)", "Indie Mix",
  "Mixed Breed", "Other"];

const defaultVisitData = {
  symptoms: "",
  diagnosis: "",
  weight: "",
  temperature: "",
  heartRate: "",
  examination: "",
  age: "",
  ageValue: "",
  ageUnit: "Years",
  notes: "",
  petOwnerBehavior: "",
  bloodTests: [
    {
      testName: "",
      testId: null
    }
  ],
  prescriptions: [
    {
      medicineName: "",
      dosage: "",
      duration: "",
      instructions: ""
    }
  ], reports: [
    {
      reportType: "",
      file: null
    }
  ]
};



function App() {
  const [showBilling, setShowBilling] = useState(false); // 🔥 ADD THIS
  const [showBloodTestModal, setShowBloodTestModal] = useState(false);
  const [selectedPetForBloodTest, setSelectedPetForBloodTest] = useState(null);
  const [showEditPetModal, setShowEditPetModal] = useState(false);
  const [selectedPetForEdit, setSelectedPetForEdit] = useState(null);
  const [editPetErrors, setEditPetErrors] = useState({});
  const [appointmentCount, setAppointmentCount] = useState(0);
  const [appointments, setAppointments] = useState([]);
  const [editPetData, setEditPetData] = useState({
    name: "",
    age: "",
    ageUnit: "Years",
    weight: ""
  });
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [visitErrors, setVisitErrors] = useState({});
  const [newlyCreatedPet, setNewlyCreatedPet] = useState(null);
  const [showAddStock, setShowAddStock] = useState(false);
  const [scannedBarcode, setScannedBarcode] = useState("");
  const [errors, setErrors] = useState({});
  const user = JSON.parse(localStorage.getItem("user"));
  const [mobile, setMobile] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [results, setResults] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [allMedicines, setAllMedicines] = useState([]);
  const [medicineSuggestions, setMedicineSuggestions] = useState({});
  const [allBloodTests, setAllBloodTests] = useState([]);
  const [bloodTestSuggestions, setBloodTestSuggestions] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visitData, setVisitData] = useState({ ...defaultVisitData });
  const [showStockModal, setShowStockModal] = useState(false);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [selectedPetForAppointment, setSelectedPetForAppointment] = useState(null);
  const [showAppointmentsModal, setShowAppointmentsModal] = useState(false);
  const [showRecentBills, setShowRecentBills] = useState(false);
  const [showSalesReport, setShowSalesReport] = useState(false);
  const [showGstItemsReport, setShowGstItemsReport] = useState(false);
  const [showBloodTestAdminModal, setShowBloodTestAdminModal] = useState(false);
  const [showMiscBillModal, setShowMiscBillModal] = useState(false);
  const [showOnlineConsultation, setShowOnlineConsultation] = useState(false);
  const [showGooglePosts, setShowGooglePosts] = useState(false);
  const [appNotices, setAppNotices] = useState([]);
  const [activeNoticeIndex, setActiveNoticeIndex] = useState(0);
  const [sessionLogoutAt, setSessionLogoutAt] = useState(null);
  const [sessionCountdown, setSessionCountdown] = useState("");
  const [activeTab, setActiveTab] = useState("matrix");
  const [gbpInitialTab, setGbpInitialTab] = useState("matrix");
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const [keywordYear, setKeywordYear] = useState(
    Number(getISTDateParts().year)
  );

  const [keywordMonth, setKeywordMonth] = useState(
    Number(getISTDateParts().month) - 1
  );

  const [searchKeywords, setSearchKeywords] = useState([]);

  const [keywordLoading, setKeywordLoading] = useState(false);

  const [keywordError, setKeywordError] = useState("");

  const [keywordSearch, setKeywordSearch] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    species: "",
    breed: "",
    gender: "",
    age: "",
    ageUnit: "Years",
    color: "",
    weight: ""
  });
  const [showVisitsModal, setShowVisitsModal] = useState(false);
  const [visits, setVisits] = useState([]);
  const [visitTestDetails, setVisitTestDetails] = useState({});
  const [pendingVisitDeleteId, setPendingVisitDeleteId] = useState(null);
  const [selectedVisitContext, setSelectedVisitContext] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [file, setFile] = useState(null);
  const [reportType, setReportType] = useState("");
  const [showGBPMatrix, setShowGBPMatrix] = useState(false);
  const [showLeaveTracker, setShowLeaveTracker] = useState(false);

  const handleClose = () => {
    setShowModal(false);
  };

  const showAppNotice = (type, message) => {
    setAppNotices((prev) => {
      if (prev.some((notice) => notice.type === type && notice.message === message)) {
        return prev;
      }

      const next = [...prev, { id: `${Date.now()}-${Math.random()}`, type, message }];
      return next.slice(-8);
    });
    setActiveNoticeIndex(Math.max(0, Math.min(appNotices.length, 7)));
  };

  useEffect(() => {
    if (appNotices.length <= 1) return undefined;

    const interval = window.setInterval(() => {
      setActiveNoticeIndex((prev) => (prev + 1) % appNotices.length);
    }, 4000);

    return () => window.clearInterval(interval);
  }, [appNotices.length]);

  const dismissAppNotice = (id) => {
    setAppNotices((prev) => {
      const next = prev.filter((notice) => notice.id !== id);
      return next;
    });
    setActiveNoticeIndex(0);
  };

  const currentAppNotice = appNotices[activeNoticeIndex] || appNotices[0] || null;


  useEffect(() => {
    const user = localStorage.getItem("user");
    if (user) {
      setIsLoggedIn(true);
    }
  }, []);


  const filterBloodTests = (query, index) => {

    if (!query || query.length < 2) {
      setBloodTestSuggestions((prev) => ({
        ...prev,
        [index]: []
      }));
      return;
    }

    const filtered = allBloodTests.filter((t) =>
      t.profileParticulars
        ?.toLowerCase()
        .includes(query.toLowerCase())
    );

    setBloodTestSuggestions((prev) => ({
      ...prev,
      [index]: filtered.slice(0, 8)
    }));
  };


  const handleBloodTestChange = (index, e) => {

    const updated = [...visitData.bloodTests];

    updated[index][e.target.name] = e.target.value;

    setVisitData({
      ...visitData,
      bloodTests: updated
    });

    if (e.target.name === "testName") {
      filterBloodTests(e.target.value, index);
    }
  };


  const addBloodTestRow = () => {

    setVisitData({
      ...visitData,
      bloodTests: [
        ...visitData.bloodTests,
        {
          testName: "",
          testId: null
        }
      ]
    });
  };


  const removeBloodTestRow = (index) => {

    const updated = [...visitData.bloodTests];

    if (updated.length === 1) return;

    updated.splice(index, 1);

    setVisitData({
      ...visitData,
      bloodTests: updated
    });
  };

  const fetchAllBloodTests = async () => {
    try {
      const token = localStorage.getItem("token");

      if (!token) {
        return;
      }

      const res = await fetch("/v1/labTest/clinic", {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const data = await res.json();

      setAllBloodTests(data);

    } catch (err) {
      console.error(err);
      showAppNotice("danger", "Cannot load lab tests right now. Please check the backend server.");
    }
  };

  useEffect(() => {
    if (!isLoggedIn) return;
    fetchAllBloodTests();
  }, [isLoggedIn]);

  useEffect(() => {
    let timer;
    let warningTimer;

    const resetTimer = () => {
      clearTimeout(timer);
      clearTimeout(warningTimer);

      const logoutAt = Date.now() + 35 * 60 * 1000;
      setSessionLogoutAt(logoutAt);
      setSessionCountdown("");

      // Show popup after 30 min of inactivity
      warningTimer = setTimeout(() => {
        setShowSessionModal(true);
      }, 30 * 60 * 1000);

      // Auto logout after 35 min of inactivity
      timer = setTimeout(() => {
        handleLogout();
      }, 35 * 60 * 1000);
    };

    const events = ["mousemove", "keydown", "click"];

    events.forEach((event) => {
      window.addEventListener(event, resetTimer);
    });

    resetTimer();

    return () => {
      clearTimeout(timer);
      clearTimeout(warningTimer);
      events.forEach((event) => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, []);

  useEffect(() => {
    if (!showSessionModal || !sessionLogoutAt) return;

    const updateCountdown = () => {
      const remainingMs = sessionLogoutAt - Date.now();

      if (remainingMs <= 0) {
        setSessionCountdown("00:00");
        return;
      }

      const totalSeconds = Math.ceil(remainingMs / 1000);
      const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
      const seconds = String(totalSeconds % 60).padStart(2, "0");

      setSessionCountdown(`${minutes}:${seconds}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [showSessionModal, sessionLogoutAt]);


  const handleAddPrescription = (pet) => {
    setSelectedPet(pet);
    setVisitData({ ...defaultVisitData });
    setVisitErrors({});
    setShowModal(true);
  };

  const openEditPetModal = (pet) => {
    const parsedAge = parseAgeString(pet?.age);

    setSelectedPetForEdit(pet);
    setEditPetData({
      name: pet?.name || "",
      age: parsedAge.ageValue,
      ageUnit: parsedAge.ageUnit,
      weight: pet?.weight || ""
    });
    setEditPetErrors({});
    setShowEditPetModal(true);
  };

  const closeEditPetModal = () => {
    setShowEditPetModal(false);
    setSelectedPetForEdit(null);
    setEditPetErrors({});
    setEditPetData({
      name: "",
      age: "",
      ageUnit: "Years",
      weight: ""
    });
  };

  const handleEditPetChange = (e) => {
    const { name, value } = e.target;
    setEditPetData((prev) => ({ ...prev, [name]: value }));
    setEditPetErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSaveEditedPet = async () => {
    const newErrors = {};

    if (!editPetData.name.trim()) newErrors.name = "Pet name is required";
    if (!editPetData.age.trim()) newErrors.age = "Age is required";
    if (!editPetData.weight.trim()) newErrors.weight = "Weight is required";

    setEditPetErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    if (!selectedPetForEdit?.id) {
      showAppNotice("danger", "Unable to update pet right now.");
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const payload = {
        mobileNumber: selectedPetForEdit.mobileNumber || mobile,
        name: editPetData.name.trim(),
        species: selectedPetForEdit.species || "",
        breed: selectedPetForEdit.breed || "",
        gender: selectedPetForEdit.gender || "",
        age: buildAgeString(editPetData.age, editPetData.ageUnit),
        color: selectedPetForEdit.color || "",
        weight: editPetData.weight.trim()
      };

      const res = await fetch(`${BASE_URL}/pets/${selectedPetForEdit.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        throw new Error(`Failed to update pet (${res.status})`);
      }

      showAppNotice("success", "Pet details updated successfully");
      closeEditPetModal();
      handleSearch();
    } catch (err) {
      console.error(err);
      showAppNotice("danger", "Could not update pet details right now.");
    } finally {
      setLoading(false);
    }
  };

  const refreshAppointments = () => {
    setRefreshKey(prev => prev + 1);
  };

  // 🔥 NEW: for appointment flow
  const handleAddPrescriptionFromAppointment = (appointment) => {
    const pet = {
      id: appointment.petId,
      name: appointment.petName
    };

    handleAddPrescription(pet); // reuse logic
  };

  const handleStay = () => {
    setShowSessionModal(false);
    setSessionCountdown("");
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.reload();
  };

  const handleLogin = () => {
    setIsLoggedIn(true);
    setMobile("");
    setResults(null);
    setShowForm(false);
  };

  const filterMedicines = (query, index) => {
    if (!query || query.length < 2) {
      setMedicineSuggestions((prev) => ({ ...prev, [index]: [] }));
      return;
    }

    const filtered = allMedicines.filter((m) =>
      m.name.toLowerCase().includes(query.toLowerCase())
    );

    setMedicineSuggestions((prev) => ({
      ...prev,
      [index]: filtered.slice(0, 8)
    }));
  };

  useEffect(() => {
    const loadTodayAppointments = async () => {
      try {
        const localDate = getISTDateInputValue();

        const res = await fetch(
          `https://api-gshf6jfmfq-uc.a.run.app/api/somerandomnumberappointments?date=${localDate}`
        );

        if (!res.ok) {
          setAppointments([]);
          setAppointmentCount(0);
          return;
        }

        const data = await res.json();

        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.appointments)
            ? data.appointments
            : Array.isArray(data?.content)
              ? data.content
              : Array.isArray(data?.data)
                ? data.data
                : [];

        // Store today's appointments
        setAppointments(list);

        // Notification badge count
        setAppointmentCount(list.length);

      } catch (error) {
        console.error("Unable to load appointment notification", error);
        setAppointments([]);
        setAppointmentCount(0);
      }
    };

    loadTodayAppointments();

    // Refresh every minute
    const interval = setInterval(loadTodayAppointments, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const token = localStorage.getItem("token");

        // ✅ token missing
        if (!token) {
          console.error("No token found");
          return;
        }

        const res = await fetch("/v1/doctors", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        });

        // ✅ Handle unauthorized
        if (res.status === 401 || res.status === 403) {
          console.error("Session expired");

          localStorage.clear();

          showAppNotice("danger", "Session expired. Please login again.");

          window.location.reload();

          return;
        }

        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }

        const data = await res.json();

        if (Array.isArray(data)) {
          setDoctors(data);
        } else if (Array.isArray(data.data)) {
          setDoctors(data.data);
        } else {
          setDoctors([]);
        }

      } catch (err) {
        console.error("Doctor fetch failed", err);
        setDoctors([]);
        showAppNotice("danger", "Cannot load doctor list right now. Please check the backend server.");
      }
    };

    fetchDoctors();
  }, []);

  useEffect(() => {
    if (!isLoggedIn) return;
    const cached = localStorage.getItem("medicines");

    if (cached) {
      setAllMedicines(JSON.parse(cached));
    } else {
      fetchAllMedicines();
    }
  }, [isLoggedIn]);



  const fetchAllMedicines = async () => {
    try {

      const token = localStorage.getItem("token");

      if (!token) {
        return;
      }

      const res = await fetch(`/v1/api/medicines`, {
        headers: {
          Authorization: "Bearer " + token
        }
      });


      const data = await res.json();

      setAllMedicines(data);
      localStorage.setItem("medicines", JSON.stringify(data));
    } catch (err) {
      console.error(err);
      showAppNotice("danger", "Cannot load medicines right now. Please check the backend server.");
    }
  };


  const removeMedicineRow = (index) => {
    const updated = [...visitData.prescriptions];

    // prevent removing last row (optional)
    if (updated.length === 1) return;

    updated.splice(index, 1);

    setVisitData({
      ...visitData,
      prescriptions: updated
    });
  };

  const handleReportChange = (index, e) => {
    const updated = [...visitData.reports];

    if (e.target.name === "file") {
      updated[index].file = e.target.files[0];
    } else {
      updated[index][e.target.name] = e.target.value;
    }

    setVisitData({ ...visitData, reports: updated });
  };

  const addReportRow = () => {
    setVisitData({
      ...visitData,
      reports: [...visitData.reports, { reportType: "", file: null }]
    });
  };

  const canAccessBilling = [3, 1].includes(user?.role);
  const handlePrescription = (appointment) => {
    console.log("Selected appointment:", appointment);
  };

  const removeReportRow = (index) => {
    const updated = [...visitData.reports];
    if (updated.length === 1) return;

    updated.splice(index, 1);
    setVisitData({ ...visitData, reports: updated });
  };

  const handleDownload = async (key, fileName) => {
    try {
      const response = await fetch(
        `/v1/pets/reports/download?key=${encodeURIComponent(key)}`
      );

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();

      // 🔥 Create temporary download link
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName || "report";
      document.body.appendChild(a);
      a.click();

      // cleanup
      a.remove();
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error(err);
      showAppNotice("danger", "Failed to download file");
    }
  };

  const isNew = (createdAt) => {
    const now = new Date();
    const created = parseISTTimestamp(createdAt);

    const diffInMinutes = (now.getTime() - created) / (1000 * 60);

    return diffInMinutes <= 5; // ✅ only last 5 mins
  };

  const handleVisitChange = (e) => {
    const { name, value } = e.target;

    setVisitData({ ...visitData, [name]: value });

    if (visitErrors[name] || (name === "ageValue" && visitErrors.age) || (name === "ageUnit" && visitErrors.age)) {
      setVisitErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        if (name === "ageValue" || name === "ageUnit") {
          delete next.age;
        }
        return next;
      });
    }
  };

  const focusVisitErrorField = (field) => {
    const selectorMap = {
      age: '[name="ageValue"]',
      weight: '[name="weight"]',
      temperature: '[name="temperature"]',
      heartRate: '[name="heartRate"]',
      symptoms: '[name="symptoms"]',
      diagnosis: '[name="diagnosis"]',
      examination: '[name="examination"]',
      notes: '[name="notes"]'
    };

    const selector = selectorMap[field];
    if (!selector) return;

    const element = document.querySelector(selector);
    if (!element) return;

    element.scrollIntoView({
      behavior: "smooth",
      block: "center"
    });

    if (typeof element.focus === "function") {
      element.focus();
    }
  };

  const handlePrescriptionChange = (index, e) => {
    const updated = [...visitData.prescriptions];
    updated[index][e.target.name] = e.target.value;

    setVisitData({ ...visitData, prescriptions: updated });

    // 🔥 NEW: trigger autocomplete
    if (e.target.name === "medicineName") {
      filterMedicines(e.target.value, index);
    }
  };

  const addMedicineRow = () => {
    setVisitData({
      ...visitData,
      prescriptions: [
        ...visitData.prescriptions,
        { medicineName: "", dosage: "", duration: "", instructions: "" }
      ]
    });
  };

  const handleEditVisit = (visit) => {
    const parsedAge = parseAgeString(visit.age);

    setVisitData({
      ...defaultVisitData,
      ...visit,
      visitId: visit.id,
      prescriptions: visit.prescriptions || defaultVisitData.prescriptions,
      bloodTests: visit.bloodTests || visit.bloodTest || defaultVisitData.bloodTests,
      petOwnerBehavior: visit.petOwnerBehavior || visit.petOwnerbehavior || "",
      ageValue: parsedAge.ageValue,
      ageUnit: parsedAge.ageUnit,
      reports: (visit.report || []).map(r => ({
        reportType: r.reportType,
        file: null
      }))
    });
    setVisitErrors({});

    setShowVisitsModal(false); // close history
    setSelectedVisitContext(null);
    setShowModal(true); // open edit modal
  };

  const handleDeletePrescriptionFromVisit = (visitIndex, prescriptionIndex) => {
    setVisits((prevVisits) =>
      prevVisits.map((visit, idx) => {
        if (idx !== visitIndex) return visit;

        const currentPrescriptions = visit.prescriptions || [];
        return {
          ...visit,
          prescriptions: currentPrescriptions.filter((_, pIdx) => pIdx !== prescriptionIndex)
        };
      })
    );
  };

  const handleDeleteVisit = async (visitId) => {
    try {
      const token = localStorage.getItem("token");

      const res = await fetch(
        `${BASE_URL}/pets/visits/${visitId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (!res.ok) {
        throw new Error("Failed to delete visit");
      }

      setVisits((prev) => prev.filter((visit) => visit.id !== visitId));
      showAppNotice("success", "Visit deleted successfully");
    } catch (err) {
      console.error(err);
      showAppNotice("danger", "Failed to delete visit");
    }
  };

  const requestDeleteVisit = (visitId) => {
    setPendingVisitDeleteId(visitId);
  };

  const cancelDeleteVisit = () => {
    setPendingVisitDeleteId(null);
  };

  const confirmDeleteVisit = async () => {
    if (!pendingVisitDeleteId) return;
    const visitId = pendingVisitDeleteId;
    setPendingVisitDeleteId(null);
    await handleDeleteVisit(visitId);
  };

  const cleanedBloodTests = visitData.bloodTests.filter(
    (b) => b.testName?.trim() || b.testId
  );

  const cleanedPrescriptions = visitData.prescriptions || defaultVisitData.prescriptions;
  const cleanedReports = visitData.reports || defaultVisitData.reports;
  const finalVisitAge = visitData.ageValue
    ? buildAgeString(visitData.ageValue, visitData.ageUnit)
    : String(visitData.age || "").trim();

  const isBlank = (value) => String(value ?? "").trim() === "";

  const hasVisitError = (field) => Boolean(visitErrors[field]);
  const fieldClass = (baseClass, field) =>
    `${baseClass} ${hasVisitError(field) ? "is-invalid" : ""}`;


  const handleSaveVisit = async () => {

    const newErrors = {};

    // 🔥 Visit fields
    if (isBlank(visitData.weight)) newErrors.weight = "Weight is required";
    if (isBlank(visitData.temperature)) newErrors.temperature = "Temperature is required";
    if (isBlank(visitData.ageValue) && isBlank(visitData.age)) newErrors.age = "Age is required";
    if (isBlank(visitData.heartRate)) newErrors.heartRate = "Heart rate is required";
    if (isBlank(visitData.symptoms)) newErrors.symptoms = "Symptoms are required";
    if (isBlank(visitData.diagnosis)) newErrors.diagnosis = "Diagnosis is required";
    if (isBlank(visitData.examination)) newErrors.examination = "Examination is required";
    if (isBlank(visitData.notes)) newErrors.notes = "Notes are required";

    setVisitErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = ["weight", "temperature", "age", "heartRate", "symptoms", "diagnosis", "examination", "notes"]
        .find((field) => newErrors[field]);

      window.setTimeout(() => focusVisitErrorField(firstErrorField), 0);
      return;
    }

    try {
      const formData = new FormData();

      // ✅ FULL VISIT OBJECT
      const { weightUnit, ...visitDataWithoutWeightUnit } = visitData;

      const visitPayload = {
        petId: selectedPet.id,
        visitId: visitData.visitId || visitData.id || null,

        doctorId: user?.id,
        clinicId: user?.clinicId,
        docName: user?.name,

        ...visitDataWithoutWeightUnit,

        // Convert weight + unit into one backend value
        weight: visitData.weight
          ? `${visitData.weight} ${weightUnit === "GRAMS" ? "grams" : "kg"}`
          : "",

        age: finalVisitAge,

        bloodTests: cleanedBloodTests,
        prescriptions: cleanedPrescriptions,
        reports: cleanedReports
          .filter(r => r.reportType || r.file)
          .map(r => ({
            reportType: r.reportType
          }))
      };

      formData.append(
        "visit",
        new Blob(
          [JSON.stringify(visitPayload)],
          { type: "application/json" }
        )
      );

      // ✅ FILES
      cleanedReports.forEach((r) => {
        if (r.file) {
          formData.append("files", r.file);
        }
      });

      const token = localStorage.getItem("token");

      const res = await fetch(`${BASE_URL}/pets/visits`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      if (res.ok) {
        showAppNotice("success", "Saved successfully");
        setShowModal(false);
        setVisitData({ ...defaultVisitData });
      }

    } catch (err) {
      console.error(err);
    }
  };




  const handleViewVisits = async (pet, visitContext = null) => {
    try {
      setSelectedPet(pet); // 🔥 IMPORTANT
      setSelectedVisitContext(visitContext);
      setVisitTestDetails({});

      const token = localStorage.getItem("token");

      const response = await fetch(
        `/v1/pets/visits/${pet.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await response.json();

      const sortedVisits = Array.isArray(data)
        ? [...data].sort((a, b) => {
          const aDate = parseISTTimestamp(a.visitDate || a.createdAt) || 0;
          const bDate = parseISTTimestamp(b.visitDate || b.createdAt) || 0;
          return bDate - aDate;
        })
        : [];

      const uniqueTestIds = [
        ...new Set(
          sortedVisits.flatMap((visit) =>
            (visit.bloodTest || []).map((test) => test.testId).filter(Boolean)
          )
        )
      ];

      const detailPairs = await Promise.all(
        uniqueTestIds.map(async (testId) => {
          const testRes = await fetch(`${BASE_URL}/labTest/${testId}`, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          if (!testRes.ok) {
            return [testId, null];
          }

          const testData = await testRes.json();
          return [testId, testData];
        })
      );

      const detailMap = detailPairs.reduce((acc, [testId, testData]) => {
        if (testData) {
          acc[testId] = testData;
        }
        return acc;
      }, {});

      setVisitTestDetails(detailMap);
      setVisits(sortedVisits);
      setShowVisitsModal(true);
    } catch (err) {
      console.error("Error fetching visits", err);
    }
  };

  const handleBookAppointment = (pet) => {
    setSelectedPetForAppointment(pet);
    setShowAppointmentModal(true);
  };

  const handleUpload = async () => {
    if (!file || !reportType) {
      showAppNotice("danger", "Select file and report type");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("reportType", reportType);

    await fetch(
      `${BASE_URL}/visits/${selectedPet.id}/upload-report`,
      {
        method: "POST",
        body: formData
      }
    );

    showAppNotice("success", "Report uploaded");
  };

  const handleSearch = async () => {
    const newErrors = {};

    if (!isValidMobile(mobile)) {
      newErrors.mobile = "Enter valid 10-digit mobile number";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);

    try {
      const token = localStorage.getItem("token"); // ✅ get token

      const res = await fetch(`${BASE_URL}/pets/search?mobile=${mobile}`, {
        headers: {
          "Authorization": "Bearer " + token // ✅ attach token
        }
      });

      if (!res.ok) {
        console.error("Error:", res.status);
        showAppNotice("danger", "Unauthorized or session expired");
        return;
      }

      const data = await res.json();

      const sortedData = data.sort(
        (a, b) => parseISTTimestamp(b.createdAt) - parseISTTimestamp(a.createdAt)
      );

      setResults(sortedData);
      setShowForm(data.length === 0);

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };



  if (!isLoggedIn) {
    return <Login onLogin={handleLogin} />;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    const newErrors = {};

    if (!formData.name) newErrors.name = "Pet name is required";
    if (!formData.species) newErrors.species = "Species is required";
    if (!formData.gender) newErrors.gender = "Gender is required";

    if (!mobile || !isValidMobile(mobile)) {
      newErrors.mobile = "Valid mobile number required";
    }

    setErrors(newErrors);
    // ❌ stop if any error
    if (Object.keys(newErrors).length > 0) return;

    setLoading(true);

    try {
      const payload = {
        mobileNumber: mobile,
        name: formData.name,
        species: formData.species,
        breed: formData.breed,
        gender: formData.gender,
        age: buildAgeString(formData.age, formData.ageUnit),
        color: formData.color,
        weight: formData.weight
          ? `${formData.weight} ${formData.weightUnit === "GRAMS" ? "grams" : "kg"}`
          : ""
      };

      const token = localStorage.getItem("token");

      const res = await fetch(`${BASE_URL}/pets/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {

        const savedPet = await res.json();

        showAppNotice("success", "Pet registered successfully");

        // save newly created pet
        setNewlyCreatedPet(savedPet);

        // OPTIONAL
        // refresh pet list
        handleSearch();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false); // ✅ CRITICAL FIX
    }
  };

  return (
    <div className="app-shell">
      <div className="topbar d-flex justify-content-between align-items-center p-3">

        <div className="topbar-brand">
          <strong>
            VetOps
            {user?.clinicName && (
              <span className="ms-2 text-muted" style={{ fontSize: "14px" }}>
                | {user.clinicName}
              </span>
            )}

          </strong>

          <button
            type="button"
            className="mobile-nav-toggle"
            onClick={() => setIsMobileNavOpen((isOpen) => !isOpen)}
            aria-label={isMobileNavOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={isMobileNavOpen}
          >
            <Menu size={22} aria-hidden="true" />
          </button>
        </div>

        <div className="d-flex align-items-center gap-3 topbar-actions">
          <UserProfileModal user={user} />
          {appointmentCount > 0 && (
            < AppointmentNotification />
          )}
          <button
            className="btn btn-danger"
            onClick={() => {
              localStorage.removeItem("user");
              setIsLoggedIn(false);
              setMobile("");
              setResults(null);
              setShowForm(false);
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {isMobileNavOpen && (
        <button
          type="button"
          className="mobile-nav-backdrop"
          onClick={() => setIsMobileNavOpen(false)}
          aria-label="Close navigation"
        />
      )}

      <aside
        className={`side-nav-panel ${isMobileNavOpen ? "is-mobile-open" : ""}`}
        onClick={(event) => {
          if (event.target.closest("button")) {
            setIsMobileNavOpen(false);
          }
        }}
      >
        <div className="side-nav-title">Navigation</div>
        <div className="side-nav-stack">
          {canAccessBilling && (
            <>
              <button
                className="btn btn-primary side-nav-button"
                onClick={() => setShowStockModal(true)}
              >
                Add Stock
              </button>

              <button
                className="btn btn-primary side-nav-button"
                onClick={() => setShowBilling(true)}
              >
                Bill
              </button>

              <button
                className="btn btn-primary side-nav-button"
                onClick={() => setShowMiscBillModal(true)}
              >
                Print Miscellaneous Bills
              </button>

              <button
                className="btn btn-primary side-nav-button"
                onClick={() => setShowExpenseModal(true)}
              >
                Add Expense
              </button>

              <button
                className="btn btn-primary side-nav-button"
                onClick={() => setShowRecentBills(true)}
              >
                Recent Bills
              </button>
            </>
          )}

          {[1].includes(Number(user?.role)) && (
            <>
              {Number(user?.role) === 1 && (
                <button
                  className="btn btn-warning side-nav-button"
                  onClick={() => setShowBloodTestAdminModal(true)}
                >
                  Lab Test
                </button>
              )}

              <button
                className="btn btn-primary side-nav-button"
                onClick={() => setShowRegister(true)}
              >
                Register User
              </button>

              <button
                className="btn btn-success side-nav-button"
                onClick={() => setShowSalesReport(true)}
              >
                Sales Report
              </button>

              <button
                type="button"
                className="btn btn-primary side-nav-button"
                onClick={() => setShowLeaveTracker(true)}
              >
                Leaves Tracker
              </button>

              <button
                className="btn btn-success side-nav-button"
                onClick={() => setShowGstItemsReport(true)}
              >
                GST Report
              </button>

              <button
                className="btn btn-primary side-nav-button"
                onClick={() => setShowUsersModal(true)}
              >
                View Users
              </button>

              <button
                type="button"
                className="btn btn-primary side-nav-button"
                onClick={() => setShowGBPMatrix(true)}
              >
                📊 GBP Matrix
              </button>

            </>
          )}

          {[1, 2].includes(Number(user?.role)) && (
            <button
              className="btn btn-primary side-nav-button"
              onClick={() => setShowOnlineConsultation(true)}
            >
              Online Consultation
            </button>
          )}



        </div>
      </aside>

      {currentAppNotice && (
        <div className="container app-main mt-3">
          <div className={`alert alert-${currentAppNotice.type} d-flex align-items-center justify-content-between gap-3 mb-0`}>
            <div className="d-flex align-items-center gap-3 flex-grow-1">
              <span className="fw-semibold text-nowrap">
                {appNotices.length > 1 ? `${activeNoticeIndex + 1}/${appNotices.length}` : "Notice"}
              </span>
              <span>{currentAppNotice.message}</span>
            </div>

            <div className="d-flex align-items-center gap-2">
              {appNotices.length > 1 && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark"
                    onClick={() =>
                      setActiveNoticeIndex((prev) => (prev - 1 + appNotices.length) % appNotices.length)
                    }
                  >
                    Prev
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-dark"
                    onClick={() =>
                      setActiveNoticeIndex((prev) => (prev + 1) % appNotices.length)
                    }
                  >
                    Next
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn-close"
                onClick={() => dismissAppNotice(currentAppNotice.id)}
              />
            </div>
          </div>
        </div>
      )}

      {showRegister && (
        <>
          <div className="modal-backdrop fade show"></div>

          <div className="modal show fade d-block" tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">

                <div className="modal-header">
                  <button
                    className="btn-close"
                    onClick={() => setShowRegister(false)}
                  ></button>
                </div>

                <div className="modal-body">
                  <RegisterUser
                    onClose={() => setShowRegister(false)}
                    clinicId={user?.clinicId}
                  />
                </div>

              </div>
            </div>
          </div>
        </>
      )}
      {/* ✅ session timeout modal */}
      <SessionTimeoutModal
        show={showSessionModal}
        onStay={handleStay}
        onLogout={handleLogout}
        countdown={sessionCountdown}
      />
      {/* ✅ session timeout modal */}
      <StockModal
        show={showStockModal}
        onClose={() => setShowStockModal(false)}

        // 🔥 pass scanned barcode
        initialBarcode={scannedBarcode}
      />

      <AppointmentModal
        show={showAppointmentModal}   // ✅ FIXED
        onClose={() => {

          setShowAppointmentModal(false);

          // optional cleanup
          setNewlyCreatedPet(null);

          setShowForm(false);
        }}
        pet={selectedPetForAppointment}   // ✅ FIXED
        clinicName={user?.clinicName}
        clinic={user?.clinic}
        onAppointmentCreated={refreshAppointments}
      />

      <BillingModal
        show={showBilling}
        clinicName={user?.clinicName}
        clinic={user?.clinic}
        onClose={() => setShowBilling(false)}
        onAddNewProduct={(barcode) => {
          setScannedBarcode(barcode);
          setShowStockModal(true);
        }}
      />

      <MiscellaneousBillModal
        show={showMiscBillModal}
        onClose={() => setShowMiscBillModal(false)}
        clinicName={user?.clinicName}
        clinic={user?.clinic}
      />

      <OnlineConsultationModal
        show={showOnlineConsultation}
        onClose={() => setShowOnlineConsultation(false)}
        clinic={user?.clinic}
        speciesList={speciesList}
        breedOptions={{
          Dog: dogBreeds,
          Cat: catBreeds,
          Rabbit: rabbitBreeds,
          Bird: birdBreeds,
          Exotic: exoticBreeds
        }}
      />

      <UsersModal
        show={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        currentUserId={user?.id}
      />

      {/* ✅ FULL SCREEN LOADER */}
      {loading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.4)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999
          }}
        >
          <div className="text-center text-white">
            <div className="spinner-border" role="status"></div>
            <p className="mt-2">Processing...</p>
          </div>
        </div>
      )}

      {/* ✅ MAIN UI */}
      <div className="container app-main mt-4">
        <div className="hero-panel mb-4">
          <div>
            <p className="hero-kicker mb-2">Clinic Operations Console</p>
            <h2 className="hero-title mb-2">VetOps System</h2>
            <p className="hero-copy mb-0">
              Search pets, manage visits, and move between booking, billing, and prescriptions from one polished workspace.
            </p>
          </div>

          <div className="hero-visual">
            <div className="pet-spotlight">
              <div className="pet-card pet-card-dog">
                <div className="pet-badge">Dog</div>
                <div className="pet-icon-wrap">
                  <Dog size={42} strokeWidth={2.2} />
                </div>
                <div className="pet-caption">Healthy checkups</div>
              </div>

              <div className="pet-card pet-card-cat">
                <div className="pet-badge">Cat</div>
                <div className="pet-icon-wrap">
                  <Cat size={42} strokeWidth={2.2} />
                </div>
                <div className="pet-caption">Care with calm</div>
              </div>
            </div>
          </div>
        </div>
        <HomeAppointmentsPanel />
        <LowStockAlert user={user} onError={showAppNotice} />
        <ExpiryAlert user={user} onError={showAppNotice} />
        <FollowUpConsultation
          onViewHistory={handleViewVisits}
          clinic={user?.clinic}
          onError={showAppNotice}
        />

        {showExpenseModal && (
          <ExpenseModal
            clinicId={user?.clinicId}
            isOpen={showExpenseModal}
            onClose={() => setShowExpenseModal(false)}
          />

        )}

        {showGooglePosts && (
          <div
            className="modal fade show"
            style={{
              display: "block",
              backgroundColor: "rgba(0, 0, 0, 0.5)"
            }}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">

              <div className="modal-content">

                <div className="modal-header">

                  <h5 className="modal-title">
                    Google Posts
                  </h5>

                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowGooglePosts(false)}
                  />

                </div>

                <div className="modal-body">

                  <GooglePosts
                    onClose={() => setShowGooglePosts(false)}
                    onSuccess={(message) => {
                      // keep your existing success handling here
                      console.log(message);
                    }}
                    onError={(message) => {
                      // keep your existing error handling here
                      console.error(message);
                    }}
                  />

                </div>

              </div>

            </div>
          </div>
        )}
        {/* 🔍 Search */}

        <form
          className="card shadow-sm border-0 p-3 mb-4"
          style={{
            borderRadius: "16px",
            background: "linear-gradient(135deg, #b2cff5, #eef5ff)"
          }}
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <div className="row g-3 align-items-end">

            {/* Input */}
            <div className="col-md-9">
              <label
                className="form-label fw-semibold mb-2"
                style={{ fontSize: "14px" }}
              >
                📱 Search Customer by Mobile Number
              </label>

              <input
                type="text"
                className={`form-control form-control-lg ${errors.mobile ? "is-invalid" : ""
                  }`}
                placeholder="Enter Mobile Number"
                value={mobile}
                onChange={(e) => {
                  setMobile(e.target.value);
                  // reset state
                  setNewlyCreatedPet(null);
                }}
                style={{
                  borderRadius: "12px",
                  border: "1px solid #cfe2ff"
                }}
              />

              {errors.mobile && (
                <div className="invalid-feedback d-block">
                  {errors.mobile}
                </div>
              )}
            </div>

            {/* Button */}
            <div className="col-md-3 d-grid">
              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={!mobile || loading}
                style={{
                  borderRadius: "12px",
                  fontWeight: "600",
                  height: "48px",
                  boxShadow: "0 4px 10px rgba(13,110,253,0.25)"
                }}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Searching...
                  </>
                ) : (
                  <>🔍 Search</>
                )}
              </button>
            </div>

          </div>
        </form>



        {/* ❌ No Results */}
        {results && results.length === 0 && !showForm && (
          <p>No pets found. You can add a new pet.</p>
        )}


        {/* 📋 Results */}
        {results && results.length > 0 && (
          <div className="accordion" id="petAccordion">
            {results.map((pet, index) => (
              <div className="accordion-item" key={pet.id}>
                <h2 className="accordion-header">
                  <button
                    className="accordion-button collapsed"
                    type="button"
                    data-bs-toggle="collapse"
                    data-bs-target={`#collapse${index}`}
                  >
                    {pet.name} ({pet.species})

                    {index === 0 && (
                      <span className="badge bg-success ms-2">Recent Visit</span>
                    )}
                  </button>
                </h2>
                <div
                  id={`collapse${index}`}
                  className="accordion-collapse collapse"
                  data-bs-parent="#petAccordion"
                >
                  <div className="accordion-body">
                    <p><b>Breed:</b> {pet.breed}</p>
                    <p><b>Color:</b> {pet.color}</p>
                    <p><b>Age at first visit:</b> {formatPetAge(pet.age)}</p>
                    <p><b>Gender:</b> {pet.gender}</p>
                    <p><b>Weight:</b> {pet.weight}</p>

                    <div className="mt-3 d-flex gap-2">
                      {[3, 1].includes(user?.role) && (
                        <>
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={() => handleBookAppointment(pet)}
                          >
                            Book Appointment
                          </button>

                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => {
                              setSelectedPetForBloodTest(pet);
                              setShowBloodTestModal(true);
                            }}
                          >
                            Book Lab Test
                          </button>
                        </>
                      )}

                      {/* Doctor actions */}
                      {user?.role === 2 && (
                        <button
                          className="btn btn-primary btn-sm"
                          onClick={() => handleAddPrescription(pet)}
                        >
                          + Add Prescription
                        </button>
                      )}

                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => openEditPetModal(pet)}
                      >
                        Edit Pet
                      </button>

                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleViewVisits(pet, null)}
                      >
                        View Diagnosis
                      </button>

                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ➕ Add New Pet Button */}
        {results && results.length > 0 && (
          <div className="mt-3">
            <button
              className="btn btn-success"
              onClick={() => setShowForm(true)}
              disabled={loading}
            >
              + Register New Pet
            </button>
          </div>
        )}


        {/* 🧾 Form */}
        {showForm && (
          <div className="card mt-4 p-3">
            <h4>Book Appointment</h4>

            <div className="row g-2">
              <div className="col-md-6">
                <input className="form-control" value={mobile} readOnly />
              </div>

              <div className="col-md-6">
                <input
                  className="form-control"
                  name="name"
                  placeholder="Pet Name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <select
                  className={`form-select ${errors.species ? "is-invalid" : ""}`}
                  name="species"
                  value={formData.species}
                  onChange={handleChange}
                >
                  <div className="invalid-feedback">{errors.species}</div>
                  <option value="">Select Species</option>
                  {speciesList.map((s, i) => (
                    <option key={i} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-6">
                <select
                  className="form-select"
                  name="breed"
                  value={formData.breed}
                  onChange={handleChange}
                >
                  <option value="">Select Breed</option>

                  {formData.species === "Dog" &&
                    dogBreeds.map((b, i) => (
                      <option key={i} value={b}>{b}</option>
                    ))}

                  {formData.species === "Cat" &&
                    catBreeds.map((b, i) => (
                      <option key={i} value={b}>{b}</option>
                    ))}

                  {formData.species === "Rabbit" &&
                    rabbitBreeds.map((b, i) => (
                      <option key={i} value={b}>{b}</option>
                    ))}

                  {formData.species === "Bird" &&
                    birdBreeds.map((b, i) => (
                      <option key={i} value={b}>{b}</option>
                    ))}

                  {formData.species === "Exotic" &&
                    exoticBreeds.map((b, i) => (
                      <option key={i} value={b}>{b}</option>
                    ))}



                </select>
              </div>

              <div className="col-md-6">
                <select
                  className={`form-select ${errors.gender ? "is-invalid" : ""}`}
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </div>

              <div className="col-md-6">
                <div className="input-group">
                  <input
                    type="number"
                    min="0"
                    className="form-control"
                    name="age"
                    placeholder="Age"
                    value={formData.age}
                    onChange={handleChange}
                  />
                  <select
                    className="form-select"
                    name="ageUnit"
                    value={formData.ageUnit}
                    onChange={handleChange}
                    style={{ maxWidth: "110px" }}
                  >
                    {ageUnitOptions.map((unit) => (
                      <option key={unit} value={unit}>{unit}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="col-md-6">
                <input
                  className="form-control"
                  name="color"
                  placeholder="Color"
                  value={formData.color}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-6">
                <div className="input-group">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    className={`form-control ${errors.weight ? "is-invalid" : ""}`}
                    placeholder="Weight"
                    name="weight"
                    value={formData.weight || ""}
                    onChange={handleChange}
                  />

                  <select
                    className="form-select"
                    name="weightUnit"
                    value={formData.weightUnit || "KG"}
                    onChange={handleChange}
                    style={{ maxWidth: "110px" }}
                  >
                    <option value="KG">Kg</option>
                    <option value="GRAMS">Grams</option>
                  </select>
                </div>

                {errors.weight && (
                  <div className="text-danger small">
                    {errors.weight}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-3 d-flex gap-2">
              <button
                className="btn btn-primary"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}

        <DoctorAppointments
          key={refreshKey}
          onAddPrescription={handleAddPrescriptionFromAppointment}
          onViewHistory={handleViewVisits}
          doctors={doctors}
          onError={showAppNotice}
        />

      </div>

      <BloodTestModal
        show={showBloodTestModal}
        onClose={() => {
          setShowBloodTestModal(false);
          setSelectedPetForBloodTest(null);
        }}
        pet={selectedPetForBloodTest}
        clinicId={user?.clinicId}
      />

      {showEditPetModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show fade d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Edit Pet - {selectedPetForEdit?.name}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={closeEditPetModal}
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Pet Name</label>
                    <input
                      type="text"
                      className={`form-control ${editPetErrors.name ? "is-invalid" : ""}`}
                      name="name"
                      value={editPetData.name}
                      onChange={handleEditPetChange}
                      placeholder="Pet Name"
                    />
                    {editPetErrors.name && (
                      <div className="invalid-feedback d-block">{editPetErrors.name}</div>
                    )}
                  </div>

                  <div className="row g-3">
                    <div className="col-md-7">
                      <label className="form-label">Age</label>
                      <input
                        type="text"
                        className={`form-control ${editPetErrors.age ? "is-invalid" : ""}`}
                        name="age"
                        value={editPetData.age}
                        onChange={handleEditPetChange}
                        placeholder="Age"
                      />
                      {editPetErrors.age && (
                        <div className="invalid-feedback d-block">{editPetErrors.age}</div>
                      )}
                    </div>

                    <div className="col-md-5">
                      <label className="form-label">Age Unit</label>
                      <select
                        className="form-select"
                        name="ageUnit"
                        value={editPetData.ageUnit}
                        onChange={handleEditPetChange}
                      >
                        {ageUnitOptions.map((unit) => (
                          <option key={unit} value={unit}>{unit}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="form-label">Weight</label>
                    <input
                      type="text"
                      className={`form-control ${editPetErrors.weight ? "is-invalid" : ""}`}
                      name="weight"
                      value={editPetData.weight}
                      onChange={handleEditPetChange}
                      placeholder="Weight"
                    />
                    {editPetErrors.weight && (
                      <div className="invalid-feedback d-block">{editPetErrors.weight}</div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeEditPetModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveEditedPet}
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <BloodTestAdminModal
        show={showBloodTestAdminModal}
        onClose={() => setShowBloodTestAdminModal(false)}
        clinicId={user?.clinicId}
      />

      <RecentBillsModal
        show={showRecentBills}
        onClose={() => setShowRecentBills(false)}
        clinic={user?.clinic}
      />

      <SalesReportModal
        show={showSalesReport}
        onClose={() => setShowSalesReport(false)}
      />

      <GstItemsModal
        show={showGstItemsReport}
        onClose={() => setShowGstItemsReport(false)}
      />

      {showModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show fade d-block" tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">
                    Add Prescription - {selectedPet?.name}
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setShowModal(false);
                      setVisitData({ ...defaultVisitData });
                    }}
                  ></button>
                </div>

                <div className="modal-body">

                  {/* Visit Details */}
                  <div className="row g-2 mb-3">

                    <div className="col-md-6">
                      <div className="input-group">
                        <input
                          type="number"
                          min="0"
                          step="any"
                          className={fieldClass("form-control", "weight")}
                          name="weight"
                          placeholder="Weight"
                          value={visitData.weight || ""}
                          onChange={handleVisitChange}
                        />

                        <select
                          className="form-select"
                          name="weightUnit"
                          value={visitData.weightUnit || "KG"}
                          onChange={handleVisitChange}
                          style={{ maxWidth: "110px" }}
                        >
                          <option value="KG">Kg</option>
                          <option value="GRAMS">Grams</option>
                        </select>
                      </div>

                      {visitErrors.weight && (
                        <div className="text-danger small mt-1">
                          {visitErrors.weight}
                        </div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <input
                        className={fieldClass("form-control", "temperature")}
                        name="temperature"
                        placeholder="Temperature (Fh)"
                        value={visitData.temperature || ""}
                        onChange={handleVisitChange}
                      />
                      {visitErrors.temperature && (
                        <div className="text-danger small mt-1">{visitErrors.temperature}</div>
                      )}
                    </div>

                    {/* ✅ FIXED NAME */}
                    <div className="col-md-6">
                      <div className="input-group">
                        <input
                          type="number"
                          min="0"
                          className={fieldClass("form-control", "age")}
                          name="ageValue"
                          placeholder="Current Age"
                          value={visitData.ageValue ?? ""}
                          onChange={handleVisitChange}
                        />
                        <select
                          className="form-select"
                          name="ageUnit"
                          value={visitData.ageUnit || "Years"}
                          onChange={handleVisitChange}
                          style={{ maxWidth: "110px" }}
                        >
                          {ageUnitOptions.map((unit) => (
                            <option key={unit} value={unit}>{unit}</option>
                          ))}
                        </select>
                      </div>
                      {visitErrors.age && (
                        <div className="text-danger small mt-1">{visitErrors.age}</div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <input
                        className={fieldClass("form-control", "heartRate")}
                        name="heartRate"
                        placeholder="Heart Rate (BPM)"
                        value={visitData.heartRate || ""}
                        onChange={handleVisitChange}
                      />
                      {visitErrors.heartRate && (
                        <div className="text-danger small mt-1">{visitErrors.heartRate}</div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <textarea
                        className={fieldClass("form-control", "symptoms")}
                        name="symptoms"
                        placeholder="Symptoms/complain"
                        value={visitData.symptoms || ""}
                        onChange={handleVisitChange}
                      />
                      {visitErrors.symptoms && (
                        <div className="text-danger small mt-1">{visitErrors.symptoms}</div>
                      )}
                    </div>

                    <div className="col-md-6">
                      <textarea
                        className={fieldClass("form-control", "diagnosis")}
                        name="diagnosis"
                        placeholder="Diagnosis"
                        value={visitData.diagnosis || ""}
                        onChange={handleVisitChange}
                      />
                      {visitErrors.diagnosis && (
                        <div className="text-danger small mt-1">{visitErrors.diagnosis}</div>
                      )}
                    </div>

                    <div className="col-md-12">
                      <textarea
                        className={fieldClass("form-control", "examination")}
                        name="examination"
                        placeholder="Examination"
                        value={visitData.examination || ""}
                        onChange={handleVisitChange}
                      />
                      {visitErrors.examination && (
                        <div className="text-danger small mt-1">{visitErrors.examination}</div>
                      )}
                    </div>

                    <div className="col-md-12">
                      <textarea
                        className={fieldClass("form-control", "notes")}
                        name="notes"
                        placeholder="Notes"
                        value={visitData.notes || ""}
                        onChange={handleVisitChange}
                      />
                      {visitErrors.notes && (
                        <div className="text-danger small mt-1">{visitErrors.notes}</div>
                      )}
                    </div>

                    <div className="col-md-12">
                      <label className="form-label small fw-semibold mb-1">Pet Owner Behavior</label>
                      <div className="border rounded-3 p-2" style={{ maxHeight: "220px", overflowY: "auto" }}>
                        <div className="row g-2">
                          {petOwnerBehaviorOptions.map((option) => {
                            const checked = splitPetOwnerBehavior(visitData.petOwnerBehavior).includes(option);

                            return (
                              <div key={option} className="col-md-6">
                                <label className={`d-flex align-items-center gap-2 p-2 rounded-2 border ${checked ? "bg-warning-subtle border-warning" : "bg-white"}`} style={{ cursor: "pointer" }}>
                                  <input
                                    type="checkbox"
                                    className="form-check-input m-0"
                                    checked={checked}
                                    onChange={() =>
                                      setVisitData({
                                        ...visitData,
                                        petOwnerBehavior: togglePetOwnerBehavior(visitData.petOwnerBehavior, option)
                                      })
                                    }
                                  />
                                  <span>{option}</span>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Medicines */}
                  <h6>Medicines</h6>

                  {visitData.prescriptions.map((p, index) => (
                    <div key={index} className="row g-2 mb-2 align-items-center">

                      <div className="col-md-3">
                        <div style={{ position: "relative" }}>
                          <input
                            className="form-control"
                            name="medicineName"
                            placeholder="Medicine"
                            value={p.medicineName || ""}
                            onChange={(e) => handlePrescriptionChange(index, e)}
                            autoComplete="off"
                          />

                          {medicineSuggestions[index] &&
                            medicineSuggestions[index].length > 0 && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  background: "#fff",
                                  border: "1px solid #ddd",
                                  zIndex: 1000,
                                  maxHeight: "150px",
                                  overflowY: "auto"
                                }}
                              >
                                {medicineSuggestions[index].map((m) => (
                                  <div
                                    key={m.id}
                                    style={{
                                      padding: "6px 10px",
                                      cursor: "pointer"
                                    }}
                                    onClick={() => {
                                      const updated = [...visitData.prescriptions];
                                      updated[index].medicineName = m.name;
                                      updated[index].medicineId = m.id;

                                      setVisitData({
                                        ...visitData,
                                        prescriptions: updated
                                      });

                                      setMedicineSuggestions((prev) => ({
                                        ...prev,
                                        [index]: []
                                      }));
                                    }}
                                  >
                                    <small>
                                      {m.name}{" "}
                                      <span className="text-muted">
                                        ({m.category})
                                      </span>
                                    </small>
                                  </div>
                                ))}
                              </div>
                            )}
                        </div>
                      </div>

                      <div className="col-md-2">
                        <input
                          className="form-control"
                          name="dosage"
                          placeholder="Dosage"
                          value={p.dosage || ""}
                          onChange={(e) => handlePrescriptionChange(index, e)}
                        />
                      </div>

                      <div className="col-md-2">
                        <input
                          className="form-control"
                          name="duration"
                          placeholder="Duration"
                          value={p.duration || ""}
                          onChange={(e) => handlePrescriptionChange(index, e)}
                        />
                      </div>

                      <div className="col-md-3">
                        <input
                          className="form-control"
                          name="instructions"
                          placeholder="Instructions"
                          value={p.instructions || ""}
                          onChange={(e) => handlePrescriptionChange(index, e)}
                        />
                      </div>

                      <div className="col-md-2">
                        <button
                          className="btn btn-danger btn-sm w-100"
                          onClick={() => removeMedicineRow(index)}
                        >
                          Delete
                        </button>
                      </div>

                    </div>
                  ))}

                  <button className="btn btn-sm btn-secondary mt-2" onClick={addMedicineRow}>
                    + Add Medicine
                  </button>

                  <h6 className="mt-4">Lab Tests</h6>

                  {visitData.bloodTests.map((b, index) => (
                    <div key={index} className="row g-2 mb-2 align-items-center">

                      <div className="col-md-10">

                        <div style={{ position: "relative" }}>

                          <input
                            className="form-control"
                            name="testName"
                            placeholder="Search Lab Test"
                            value={b.testName || ""}
                            onChange={(e) => handleBloodTestChange(index, e)}
                            autoComplete="off"
                          />

                          {bloodTestSuggestions[index] &&
                            bloodTestSuggestions[index].length > 0 && (

                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  background: "#fff",
                                  border: "1px solid #ddd",
                                  zIndex: 1000,
                                  maxHeight: "200px",
                                  overflowY: "auto"
                                }}
                              >

                                {bloodTestSuggestions[index].map((t) => (

                                  <div
                                    key={t.id}
                                    style={{
                                      padding: "6px 10px",
                                      cursor: "pointer"
                                    }}
                                    onClick={() => {

                                      const updated = [...visitData.bloodTests];

                                      updated[index].testName = t.profileParticulars;
                                      updated[index].testId = t.id;

                                      setVisitData({
                                        ...visitData,
                                        bloodTests: updated
                                      });

                                      setBloodTestSuggestions((prev) => ({
                                        ...prev,
                                        [index]: []
                                      }));
                                    }}
                                  >

                                    <div>

                                      <div>
                                        <b>{t.profileParticulars}</b>
                                      </div>

                                      <div className="small text-muted">
                                        Category: {t.category}
                                      </div>

                                      <div className="small text-muted">
                                        Machine: {t.machineType}
                                      </div>

                                      <div className="small text-muted">
                                        Specimen: {t.specimenRequired}
                                      </div>

                                      <div className="small text-success">
                                        ₹ {t.actualPrice}
                                      </div>

                                    </div>

                                  </div>
                                ))}

                              </div>
                            )}
                        </div>

                      </div>

                      <div className="col-md-2">

                        <button
                          className="btn btn-danger btn-sm w-100"
                          onClick={() => removeBloodTestRow(index)}
                        >
                          Delete
                        </button>

                      </div>

                    </div>
                  ))}

                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={addBloodTestRow}
                  >
                    + Add Lab Test
                  </button>

                  <h6 className="mt-3">Reports</h6>

                  {visitData.reports.map((r, index) => (
                    <div key={index} className="row g-2 mb-2 align-items-center">

                      <div className="col-md-3">
                        <select
                          className="form-select"
                          name="reportType"
                          value={r.reportType || ""}
                          onChange={(e) => handleReportChange(index, e)}
                        >
                          <option value="">Select Type</option>
                          <option>Blood</option>
                          <option>ECG</option>
                          <option>ECO</option>
                          <option>Urine</option>
                          <option>CT</option>
                          <option>Xray</option>
                        </select>
                      </div>

                      <div className="col-md-5">
                        <input
                          type="file"
                          className="form-control"
                          name="file"
                          onChange={(e) => handleReportChange(index, e)}
                        />
                      </div>

                      <div className="col-md-2">
                        <button
                          className="btn btn-danger btn-sm w-100"
                          onClick={() => removeReportRow(index)}
                        >
                          Delete
                        </button>
                      </div>

                    </div>
                  ))}

                  <button className="btn btn-sm btn-secondary" onClick={addReportRow}>
                    + Add Report
                  </button>

                </div>

                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                    Close
                  </button>
                  <button className="btn btn-primary" onClick={handleSaveVisit}>
                    Save
                  </button>
                </div>

              </div>
            </div>
          </div>
        </>
      )}

      <ChatWindow />
      {showGBPMatrix && (
        <GBPMatrixModal user={user}
          onClose={() => setShowGBPMatrix(false)}
        />
      )}

      {showLeaveTracker && (
        <LeaveTrackerModal
          onClose={() => setShowLeaveTracker(false)}
        />
      )}


      {showVisitsModal && (
        <> <div className="modal-backdrop fade show"></div>
          <div className="modal show fade d-block" tabIndex="-1">
            <div className="modal-dialog modal-xl">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">
                    Visit History - {selectedPet?.name}
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => {
                      setPendingVisitDeleteId(null);
                      setShowVisitsModal(false);
                    }}
                  ></button>
                </div>

                <div className="modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>

                  {pendingVisitDeleteId && (
                    <div className="alert alert-warning d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2 mb-3">
                      <div>Are you sure you want to delete this entire visit?</div>
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          onClick={confirmDeleteVisit}
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={cancelDeleteVisit}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {visits.length === 0 ? (
                    <p>No visits found</p>
                  ) : (<div className="accordion" id="visitAccordion">
                    {visits.map((visit, i) => (
                      <div key={i} className="accordion-item mb-2 position-relative">

                        {/* HEADER (clickable) */}
                        <h2 className="accordion-header">
                          <button
                            className={`accordion-button ${i !== 0 ? "collapsed" : ""}`}
                            type="button"
                            data-bs-toggle="collapse"
                            data-bs-target={`#visitCollapse${i}`}
                          >
                            Visit :{i + 1} -{" "}
                            {formatISTDateTime(visit.visitDate, {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true
                            })}
                          </button>
                        </h2>

                        {user?.role !== 3 && (
                          <button
                            type="button"
                            className="visit-delete-chip"
                            title="Delete visit"
                            aria-label="Delete visit"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              requestDeleteVisit(visit.id);
                            }}
                          >
                            <Trash2 size={18} />
                          </button>
                        )}

                        {/* BODY (collapsible) */}
                        <div
                          id={`visitCollapse${i}`}
                          className="accordion-collapse collapse"
                          data-bs-parent="#visitAccordion"
                        >
                          <div className="accordion-body small" style={{ lineHeight: "1.4" }}>

                            <div><b>Symptoms:</b> {visit.symptoms}</div>
                            <div><b>Diagnosis:</b> {visit.diagnosis}</div>
                            <div><b>Visit ID:</b> {visit.id}</div>
                            <div><b>Age:</b> {formatVisitAge(visit)}</div>
                            {splitPetOwnerBehavior(visit.petOwnerBehavior || visit.petOwnerbehavior).length > 0 && (
                              <div className="mt-2 mb-2">
                                <div className="d-flex flex-wrap gap-2">
                                  <span className="badge bg-dark px-3 py-2" style={{ fontSize: "0.85rem" }}>
                                    Pet Owner Behavior
                                  </span>
                                  {splitPetOwnerBehavior(visit.petOwnerBehavior || visit.petOwnerbehavior).map((item) => (
                                    <span
                                      key={item}
                                      className="badge bg-warning text-dark px-3 py-2"
                                      style={{ fontSize: "0.85rem" }}
                                    >
                                      {item}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div><b>Weight:</b> {visit.weight}</div>
                            <div><b>Temperature:</b> {visit.temperature}</div>
                            <div><b>heartRate:</b> {visit.heartRate}</div>
                            <div><b>Examination:</b> {visit.examination}</div>
                            <div><b>Notes:</b> {visit.notes}</div>
                            <div className="mt-2"><b>Lab Tests:</b></div>


                            {visit.bloodTest && visit.bloodTest.length > 0 ? (

                              <div className="table-responsive mt-1">
                                <table className="table table-sm table-bordered mb-2">

                                  <thead className="table-light">
                                    <tr>
                                      <th>#</th>
                                      <th>Test ID</th>
                                      <th>Test Name</th>
                                      <th>Price</th>
                                    </tr>
                                  </thead>

                                  <tbody>
                                    {visit.bloodTest.map((test, idx) => (
                                      <tr key={idx}>
                                        <td>{idx + 1}</td>
                                        <td>{test.testId}</td>
                                        <td>{test.testName}</td>
                                        <td>
                                          {visitTestDetails[test.testId]?.actualPrice != null
                                            ? `₹ ${visitTestDetails[test.testId].actualPrice}`
                                            : "-"}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>

                                </table>
                              </div>

                            ) : (
                              <p>No lab tests</p>
                            )}
                            <div className="mt-2"><b>Medicines:</b></div>
                            <div className="table-responsive mt-1">
                              <table className="table table-sm table-bordered mb-1">
                                <thead className="table-light">
                                  <tr>

                                    <th>Medicine</th>
                                    <th>Dosage</th>
                                    <th>Duration</th>
                                    <th>Instructions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {visit.prescriptions?.map((med, idx) => (
                                    <tr key={idx}>
                                      <td>{med.medicineName}</td>
                                      <td>{med.dosage}</td>
                                      <td>{med.duration}</td>
                                      <td>{med.instructions}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <div className="mt-2"><b>Reports:</b></div>

                            {visit.report && visit.report.length > 0 ? (
                              <div>
                                {visit.report.map((r, idx) => (
                                  <div key={idx} className="mb-1">

                                    <span style={{ fontSize: "13px" }}>
                                      {r.reportType}
                                    </span>

                                    {" - "}

                                    <span
                                      onClick={() => handleDownload(r.filePath, r.fileName)}
                                      style={{
                                        fontSize: "13px",
                                        color: "#0d6efd",
                                        cursor: "pointer"
                                      }}
                                    >
                                      download
                                    </span>

                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p>No reports</p>
                            )}
                            <div><b>Doctor: {visit.docName}</b></div>

                            {canEditPrescriptionForAppointment(selectedVisitContext, user) && (
                              <button
                                className="btn btn-sm btn-warning mt-2"
                                onClick={() => handleEditVisit(visit)}
                              >
                                Edit Prescription
                              </button>
                            )}
                          </div>
                        </div>

                      </div>
                    ))}
                  </div>)}

                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowVisitsModal(false);
                      setSelectedVisitContext(null);
                    }}
                  >
                    Close
                  </button>
                </div>

              </div>
            </div>
          </div></>
      )}

      <footer className="app-footer">
        <div>© 2026 Himalayan Pet Clinic</div>
        <div>Developed by Himalayan Pet Clinic</div>
      </footer>
    </div>
  );
}

export default App;
